import React, { useState } from "react";
import axios from "axios";
import "../styles/Signup.css";
import ills from "../image/illustrate.png";
import logo from "../image/logoname.png";
import illsMobile from "../image/illustration.png";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Loader from "./Loader";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "./FirebaseConfig";

class ApiError extends Error {
  constructor(message, response) {
    super(message);
    this.name = "ApiError";
    this.response = response;
  }
}

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    display_name: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const parseApiError = (error) => {
    if (!error.response) {
      return "Network error. Please check your connection and try again.";
    }

    const { status, data } = error.response;

    switch (status) {
      case 400:
        if (data.email) return `Email: ${data.email.join(" ")}`;
        if (data.display_name) return `Name: ${data.display_name.join(" ")}`;
        if (data.password) return `Password: ${data.password.join(" ")}`;
        if (data.non_field_errors) return data.non_field_errors.join(" ");
        return "Invalid request. Please check your inputs.";
      
      case 409:
        return "An account with this email already exists. Please login instead.";
      
      case 500:
        return "Server error. Please try again later.";
      
      default:
        return "Something went wrong. Please try again.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
  
    try {
      const response = await axios.post(
        "https://api.nexcard.co.in/api/auth/users/", 
        formData,
        {
          validateStatus: (status) => status < 500
        }
      );

      if (response.status >= 300) {
        throw new ApiError("API request failed", response);
      }

      sessionStorage.setItem("userEmail", formData.email);
      
      navigate("/otp-verification", { 
        state: { 
          ...location.state,
          email: formData.email,
          originalPath: location.state?.from
        } 
      });
    } catch (error) {
      console.error("Signup error:", error);
      const errorMessage = parseApiError(error);
      setError(errorMessage);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Full error details:', {
          message: error.message,
          response: error.response?.data,
          stack: error.stack
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError(null);
  
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken();

      const response = await axios.get(
        "https://api.nexcard.co.in/api/auth/users/me/", 
        {
          headers: { Authorization: `Firebase ${idToken}` },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status >= 300) {
        throw new ApiError("Google API request failed", response);
      }

      sessionStorage.setItem("firebaseToken", idToken);
      sessionStorage.setItem("userEmail", user.email);
      
      const redirectPath = location.state?.from || "/home-web-holder";
      navigate("/otp-verification", { 
        state: { 
          from: redirectPath,
          email: user.email,
          sharedCardId: location.state?.from?.match(/shared-card\/([^/]+)/)?.[1]
        }
      });
    } catch (error) {
      console.error("Google Sign-Up failed:", error);
      
      if (error.response) {
        const backendError = error.response.data;
        if (backendError.detail) {
          setError(backendError.detail);
        } else if (backendError.message) {
          setError(backendError.message);
        } else {
          setError("Google authentication failed: " + JSON.stringify(backendError));
        }
      } else if (error.code) {
        switch (error.code) {
          case 'auth/account-exists-with-different-credential':
            setError("An account already exists with this email. Please login instead.");
            break;
          case 'auth/popup-closed-by-user':
            setError("Sign-up window was closed. Please try again.");
            break;
          case 'auth/network-request-failed':
            setError("Network error. Please check your internet connection.");
            break;
          default:
            setError("Google sign-up failed: " + error.message);
        }
      } else {
        setError("An unexpected error occurred during Google sign-up.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="signup-container">
      <div className="signup-left">
        <div className="logoup">
          <img src={logo} alt="NexCard Logo" />
        </div>
        <div className="mobile-illustration">
          <img src={illsMobile} alt="Mobile Illustration" />
        </div>
        <h1 className="welcomeup">Create new account</h1>
        <p className="welcomePup">Signup and start managing your Business cards now!</p>
        <form className="signup-form" onSubmit={handleSubmit}>
          <label>Full Name</label>
          <input
            type="text"
            name="display_name"
            placeholder="Enter your Full Name"
            value={formData.display_name}
            onChange={handleChange}
            required
          />
          <label>Email</label>
          <input
            type="email"
            name="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <div className="password-container4">
            <label>Password</label>
            <div className="input-wrapper4">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="******"
                value={formData.password}
                onChange={handleChange}
                required
                className="password-input4"
              />
              <span className="eye-icon4" onClick={togglePasswordVisibility}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="sign-up-btnup" disabled={isLoading}>
            {isLoading ? <Loader /> : "Create Account"}
          </button>
          <button
            type="button"
            className="google-sign-up"
            onClick={handleGoogleSignUp}
            disabled={isLoading}
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Google_Favicon_2025.svg" alt="Google Icon" /> Sign up with Google
          </button>
        </form>
        <p className="signinup">
          Already have an account?{" "}
          <button type="button" className="sign-in-btnup" onClick={() => navigate("/login")}>
            Sign in
          </button>
        </p>
      </div>
      <div className="signup-divider"></div>
      <div className="signup-right">
        <img src={ills} alt="Background Illustration" className="background-image" />
      </div>
    </div>
  );
};

export default Signup;